import {
  useRef,
  useEffect,
  useCallback,
  type FC,
  type ComponentType,
} from 'react';
import {
  type To
} from 'react-router-dom';
import {
  type ThunkDispatch
} from 'redux-thunk';
import {
  type UnknownAction
} from 'redux';
import {
  useDispatch
} from 'react-redux';
import {
  type RouterNavigateOptions
} from '@remix-run/router';
import {
  pushToGtm, reportLog
} from '@shared/analytics';
import {
  signOut,
  selectSessionKey,
  selectShortProfile,
  selectIsAuthenticated,
  useSessionSelector,
  useLazyCheckLoggedInQuery,
} from '@entities/session';
import {
  STORAGE_KEY,
  SESSION_DEBUG_LOGS_ENABLED,
  ACTIVITY_CHECK_INTERVAL_MS,
} from '../config';

type WindowActivityEvent = 'focus' | 'scroll' | 'click' | 'mousemove';

export type CheckLoggedInOptions = {
  debug?: boolean;
  navigate: (to: To | null, opts?: RouterNavigateOptions) => Promise<void>;
};

export const withCheckLoggedIn = (options?: CheckLoggedInOptions) =>
  <P extends object>(WrappedComponent: ComponentType<P>): FC<P> =>
    (props: P) => {
      const debug = options?.debug ?? SESSION_DEBUG_LOGS_ENABLED;
      const lastActivityTimestampRef = useRef<number>(Date.now());

      const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>();
      const [triggerCheckLoggedIn] = useLazyCheckLoggedInQuery();

      const sessionKey = useSessionSelector(selectSessionKey);
      const shortProfile = useSessionSelector(selectShortProfile);
      const isAuthenticated = useSessionSelector(selectIsAuthenticated);

      const parseActivityTimestamp = useCallback((timestampString?: string | null) => {
        if (!timestampString) return null;
        const timestamp = Number(timestampString);
        const currentTime = Date.now();
        if (timestamp == null || timestamp > currentTime || timestamp <= 0 || isNaN(timestamp))
          return null;
        return timestamp;
      }, []);

      const handleUserActivity = useCallback(() => {
        const currentTime = Date.now();
        if (debug) console.log('User activity detected - updating timestamp to', currentTime);
        window.localStorage.setItem(STORAGE_KEY, currentTime.toString());
        lastActivityTimestampRef.current = currentTime;
      }, [debug]);

      const handleStorageEvent = useCallback(({ key, storageArea, newValue }: StorageEvent) => {
        if (key === STORAGE_KEY && storageArea === window.localStorage) {
          if (debug) console.log('Remote tab activity detected - updating timestamp to', newValue);
          const activityTimestamp = parseActivityTimestamp(newValue);
          if (activityTimestamp !== null) lastActivityTimestampRef.current = activityTimestamp;
        }
      }, [debug, parseActivityTimestamp]);

      const checkUserInactivity = useCallback(async () => {
        if (!isAuthenticated) return false;
        const currentTime = Date.now();

        if (lastActivityTimestampRef.current + ACTIVITY_CHECK_INTERVAL_MS < currentTime) {
          if (debug) console.log('User inactive - checking session', lastActivityTimestampRef.current, currentTime);

          const { data } = await triggerCheckLoggedIn();
          if (data?.loggedIn === 'TRUE') return false;

          const pushToGtmPromise = pushToGtm({
            dataLayer: {
              event: 'app_error',
              errorType: 'AuthError',
              errorCode: 'InactivityLogout',
              errorMessage: 'User was signed out after inactivity check',
              pagePath: window.location.pathname,
              pageTitle: document.title,
              timestamp: Date.now(),
            },
          });
          pushToGtmPromise.then();

          const reportLogPromise = reportLog(sessionKey, {
            kind: 'error',
            type: 'with-check-logged-in',
            user: { partyId: shortProfile?.partyId },
            payload: {
              errorType: 'AuthError',
              errorCode: 'NotLoggedIn',
              errorMessage: '401: NotLoggedIn handled by middleware.',
            },
          });
          reportLogPromise.then();

          dispatch(signOut());

          const redirectTo = '/?' + new URLSearchParams({ login: 'true' }).toString();
          await options?.navigate(redirectTo);
          return true;
        }

        return false;
      }, [triggerCheckLoggedIn, debug, dispatch, isAuthenticated]);

      const handleIntervalCheck = useCallback(() => {
        checkUserInactivity();
      }, [checkUserInactivity]);

      useEffect(() => {
        const activityEvents: Array<WindowActivityEvent> = ['focus', 'scroll', 'click', 'mousemove'];
        activityEvents.forEach(event => window.addEventListener(event, handleUserActivity, false));
        window.addEventListener('storage', handleStorageEvent, false);
        const intervalId = window.setInterval(handleIntervalCheck, ACTIVITY_CHECK_INTERVAL_MS);

        return () => {
          activityEvents.forEach(event => window.removeEventListener(event, handleUserActivity, false));
          window.removeEventListener('storage', handleStorageEvent, false);
          window.clearInterval(intervalId);
        };
      }, [handleIntervalCheck, handleStorageEvent, handleUserActivity]);

      return <WrappedComponent {...props} />;
    };